import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { XeroLinkedService, xeroAuthType } from 'app/model/linkedservice/xero.linkedservice';
import { Metamodel } from 'app/model/base/model';
import { XeroCopySource } from 'app/model/pipeline/copysource/xero.copysource';
import { useEncryptedEndpointsRadioSwitch } from './linked-service-shared-fields';
import { format } from 'app/shared/utils/string.util';
import { xeroReservedKeys } from 'app/shared/utils/connector-metadata-static-consts';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { GlobalVariables } from 'app/services/globals';

const oAuth2Form: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'host',
        label: stringResources.ModelName_Host,
        required: true,
        placeholder: stringResources.Xero_LinkedService_Host_Placeholder,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'consumerKey',
        label: stringResources.ModelName_ClientId,
        child: {
            type: 'password',
            label: stringResources.ModelName_ClientId,
            isLabelVertical: true,
            required: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            stringResources.Xero_DisplayText,
            stringResources.ModelName_ClientId)
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'privateKey',
        label: stringResources.ModelName_ClientSecret,
        child: {
            type: 'password',
            label: stringResources.ModelName_ClientSecret,
            required: true,
            isLabelVertical: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            stringResources.Xero_DisplayText,
            stringResources.ModelName_ClientSecret)
    },
    {
        type: 'textbox',
        bind: 'tenantId',
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        label: stringResources.ModelName_TenantId,
        required: true
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'refreshToken',
        label: stringResources.ModelName_RefreshToken,
        child: {
            type: 'password',
            label: stringResources.ModelName_RefreshToken,
            required: true,
            isLabelVertical: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ]
    }
]

const connectionForm: ControlMetadata[] = [
    {
        type: 'select-switch',
        bind: 'authType',
        label: stringResources.ModelName_AuthenticationType,
        hide: '{ isOldResource }',
        options: [
            {
                label: 'OAuth 1.0',
                value: xeroAuthType.OAuth_1_0
            },
            {
                label: 'OAuth 2.0',
                value: xeroAuthType.OAuth_2_0
            }
        ],
        isLabelVertical: true,
        defaultValue: xeroAuthType.OAuth_1_0,
        cases: [
            {
                caseValue: xeroAuthType.OAuth_1_0,
                children: [
                    {
                        type: 'textbox',
                        bind: 'host',
                        label: stringResources.ModelName_Host,
                        required: true,
                        placeholder: stringResources.Xero_LinkedService_Host_Placeholder,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        bind: 'consumerKey',
                        label: stringResources.ModelName_ConsumerKey,
                        child: {
                            type: 'password',
                            label: stringResources.ModelName_ConsumerKey,
                            isLabelVertical: true,
                            required: true
                        },
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            stringResources.Xero_DisplayText,
                            stringResources.ModelName_ConsumerKey)
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        bind: 'privateKey',
                        label: stringResources.ModelName_PrivateKey,
                        child: {
                            type: 'key-file-content',
                            label: stringResources.ModelName_PrivateKey,
                            required: true,
                            isLabelVertical: true,
                            readAsText: true
                        },
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            stringResources.Xero_DisplayText,
                            stringResources.ModelName_PrivateKey)
                    }
                ]
            },
            {
                caseValue: xeroAuthType.OAuth_2_0,
                children: oAuth2Form
            }
        ]
    },
    {
        type: 'textbox',
        hide: '{ !isOldResource }',
        bind: 'host',
        label: stringResources.ModelName_Host,
        required: true,
        placeholder: stringResources.Xero_LinkedService_Host_Placeholder,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'dynamic-akv-switcher',
        hide: '{ !isOldResource }',
        bind: 'consumerKey',
        label: stringResources.ModelName_ConsumerKey,
        child: {
            type: 'password',
            label: stringResources.ModelName_ConsumerKey,
            isLabelVertical: true,
            required: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            stringResources.Xero_DisplayText,
            stringResources.ModelName_ConsumerKey)
    },
    {
        type: 'dynamic-akv-switcher',
        hide: '{ !isOldResource }',
        bind: 'privateKey',
        label: stringResources.ModelName_PrivateKey,
        child: {
            type: 'key-file-content',
            label: stringResources.ModelName_PrivateKey,
            required: true,
            isLabelVertical: true,
            readAsText: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            stringResources.Xero_DisplayText,
            stringResources.ModelName_PrivateKey)
    },
    useEncryptedEndpointsRadioSwitch,
    {
        type: 'key-val-list',
        bind: 'additionalFields',
        label: stringResources.AdditionalConnectionStringFields,
        hide: '{ isOldResource }',
        reservedKeys: xeroReservedKeys
    }
]

export const xeroMetadata: ILinkedServiceTypeMetadata = {
    type: Metamodel.modelFor(XeroLinkedService).name,
    displayName: stringResources.Xero_DisplayText,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-xero'
    },
    isFirstClass: true,
    supportDatasetV2: true,
    category: ['ServicesApps'],
    isDeprecated: GlobalVariables.showLegacy ? undefined : '{ version !== "v2" }',
    copySinkTypes: [],
    tableNameAlias: 'table',
    showWarningMessage: '{ version !== "v2" }',
    warningMessage:  format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
            stringResources.VersionLabel.toLocaleLowerCase()),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-xero',
    copySourceTypes: [Metamodel.modelFor(XeroCopySource).name],
    SHIRVersion:  {
        v2: '5.61.0.0'
    },
    controls: 
    [
         {
            type: 'radio-switch',
            isLabelVertical: true,
            label: stringResources.VersionLabel,
            required: true,
            bind: 'version',
            hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
            options: driverVersionVersionOptions,
            defaultValue: driverVersions.v2,
            cases: [
                {
                    caseValue: driverVersions.v1,
                    children: [
                        {
                            type: 'hint',
                            hintMode: 'warning',
                            description: format(
                                stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                stringResources.VersionLabel.toLocaleLowerCase()),
                            link: 'https://learn.microsoft.com/azure/data-factory/connector-xero'
                        },
                        ...connectionForm
                    ]
                },
                {
                    caseValue: driverVersions.v2,
                    children: [...oAuth2Form]
                }
            ]
        },
        ...(oAuth2Form
            .map((control) => ({
                ...control,
                hide: !GlobalVariables.showLegacy ? '{version !== "v2" }' : true
            }))
        )
    ]
};